'use client';

import { useEffect, useRef } from 'react';
import { getPostHog, initAnalytics } from './lib/posthog-client';

/**
 * Associates the current PostHog context with a `zone` group (the tenant) and
 * registers `zoneCode` as a super property so every subsequent event is sliced
 * by zone.
 *
 * Call near the top of a component that knows the zone (e.g. the room client).
 * A ref tracks the last grouped code so re-renders and effect re-runs with an
 * unchanged `zoneCode` don't re-issue the group call. Safe no-op until
 * analytics is initialised.
 */
export function useZoneGroup(zoneCode?: string, zoneName?: string): void {
  const groupedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!zoneCode || groupedCodeRef.current === zoneCode) return;

    initAnalytics();
    const ph = getPostHog();
    if (!ph) return;

    // Mark grouped only once a live client has actually received the calls, so a
    // null client never permanently pins this zone as "already grouped".
    groupedCodeRef.current = zoneCode;
    ph.group('zone', zoneCode, zoneName ? { name: zoneName } : undefined);
    ph.register({ zoneCode });
  }, [zoneCode, zoneName]);
}
